/**
 * PolicyActionGroup — ONE policy, its term, and the actions that apply to it.
 *
 * This is the surface AC-1 and AC-2 describe: a collapsible group whose header
 * carries the TERM-PRECISE policy identity (never a bare policy number). Policy
 * Number and Effective Date live at the policy-group level — outside the
 * mortgagee-action collapse — because they describe the policy, not one action.
 * The nested collapsible is the mortgagee action itself (`launch-actions-list`),
 * not an extra "Actions 1 of 1" wrapper.
 *
 * The term control always renders here, even when the contract declares the
 * identity per action: `writePatch` still routes each key to the slice that
 * declares it. Exactly ONE term control renders per launch.
 *
 * Both collapse controls default to EXPANDED (AD-5) and are INDEPENDENT —
 * collapsing the group does not change the actions list's own state. Summaries
 * show only while collapsed (CollapsibleGroup).
 */

import type { ReactNode } from "react";
import type { ConfidenceLevel } from "~/common/workflows/components/JsonSchemaForm";
import type { FieldSchema } from "~/common/workflows/schemaHelpers";
import { titleCaseKey } from "~/common/workflows/schemaHelpers";
import type { PolicyInForce } from "~/common/workflows/types/client";
import ActionCard, {
  OLD_INTEREST_NAME_KEY,
} from "~/common/workflows/launch/ActionCard";
import AddActionAffordance from "~/common/workflows/launch/AddActionAffordance";
import CollapsibleGroup from "~/common/workflows/launch/CollapsibleGroup";
import FieldHelpTooltip from "~/common/workflows/launch/FieldHelpTooltip";
import PolicyTermField, {
  type TermIdentityPlacement,
} from "~/common/workflows/launch/PolicyTermField";
import {
  mergeLaunchSlice,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";
import {
  findPolicyOption,
  formatTermDate,
  readPolicyOptions,
  readPolicyTermKey,
  toIsoDateInput,
} from "~/common/workflows/launch/policyPrefillOptions";

export const EFFECTIVE_DATE_HELP =
  "If effective date is left blank it will fallback to the effective date on the policy selected.";

const CHANGE_TYPE_SUMMARY: Record<string, string> = {
  add: "Add",
  update: "Change",
  delete: "Delete",
};

const CONFIDENCE_CHIP: Record<
  ConfidenceLevel,
  { text: string; label: string; dot: string }
> = {
  high: {
    text: "text-primary-success-600",
    label: "High",
    dot: "bg-primary-success-600",
  },
  medium: {
    text: "text-primary-warning-500",
    label: "Medium",
    dot: "bg-primary-warning-500",
  },
  low: {
    text: "text-primary-error-600",
    label: "Low",
    dot: "bg-primary-error-600",
  },
};

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The term-precise identity line.
 *
 * Built from the DECLARED term-identity keys first and from the resolved option
 * row only as a fallback, so what the header shows is what the payload will
 * carry. A header that read the option row alone could show a term the action
 * does not carry — which is the wrong-term defect wearing a label.
 */
export function describeTerm(
  keys: readonly string[],
  slice: Record<string, unknown>,
  option: PolicyInForce | null,
): { policy: string; term: string } {
  const policyKey = keys.find((k) => k === "policy_number") ?? keys[0];
  const policy =
    text(policyKey ? slice[policyKey] : undefined) ||
    text(option?.policy_number);
  const from =
    text(slice.term_effective_date) || text(option?.effective_date ?? "");
  const to =
    text(slice.term_expiration_date) || text(option?.expiration_date ?? "");
  const term =
    from || to
      ? `${formatTermDate(from) || "?"} – ${formatTermDate(to) || "?"}`
      : "";
  return { policy, term };
}

function describeActionSummary(
  action: Record<string, unknown> | undefined,
  binding: LaunchBinding,
  helperValues: Record<string, unknown>,
): string {
  if (!action) return "";
  const changeTypeKey = binding.roles.changeType;
  const changeType = changeTypeKey ? text(action[changeTypeKey]) : "";
  const changeLabel =
    CHANGE_TYPE_SUMMARY[changeType] ||
    (changeType ? titleCaseKey(changeType) : "");
  const recordsKey = binding.roles.interestRecords;
  const records = recordsKey ? action[recordsKey] : undefined;
  const firstRecord =
    Array.isArray(records) && records[0] && typeof records[0] === "object"
      ? (records[0] as Record<string, unknown>)
      : null;
  const name =
    text(helperValues[OLD_INTEREST_NAME_KEY]) ||
    text(firstRecord?.name);
  return [changeLabel, name].filter(Boolean).join(" · ");
}

export interface PolicyActionGroupProps {
  binding: LaunchBinding;
  runLevelValues: Record<string, unknown>;
  actions: Record<string, unknown>[];
  /** Where the ONE term control writes. Presentation is always policy-level. */
  placement: TermIdentityPlacement;
  policyOptions: readonly PolicyInForce[];
  writePatch: (index: number, patch: Record<string, unknown>) => void;
  onChangeAction: (index: number, next: Record<string, unknown>) => void;
  onAddAction?: () => void;
  submitting?: boolean;
  /** Projected onto the action slice by `LaunchForm`. */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  /** Projected onto the action slice by `LaunchForm`. */
  fieldLoading?: Record<string, boolean>;
  /** `validateLaunch().actionErrors`, parallel to `actions`. */
  actionErrors: Array<Record<string, string>>;
  /** Label for the run-level policy-number control, from the declared schema. */
  termFieldLabel?: string;
  /** Label for the effective-date control, from the declared schema. */
  effectiveDateLabel?: string;
  /** Label for the old-interest control, from the declared schema. */
  oldInterestLabel?: string;
  /** Rendered under the header — the run-level term control's slot. */
  headerExtra?: ReactNode;
}

export default function PolicyActionGroup({
  binding,
  runLevelValues,
  actions,
  placement,
  policyOptions,
  writePatch,
  onChangeAction,
  onAddAction,
  submitting = false,
  fieldConfidence,
  fieldLoading,
  actionErrors,
  termFieldLabel = "Policy Number",
  effectiveDateLabel = "Effective Date",
  oldInterestLabel,
}: PolicyActionGroupProps) {
  const effectiveDateKey = binding.roles.effectiveDate;
  const first = actions[0];
  // The group's identity is the FIRST action's term while the gate is closed
  // (exactly one action, exactly one policy group — PRD UI/UX).
  const identitySlice = mergeLaunchSlice(binding, runLevelValues, first);
  const option = findPolicyOption(
    readPolicyOptions(identitySlice),
    text(identitySlice.policy_number) || null,
    readPolicyTermKey(identitySlice),
  );
  const { policy, term } = describeTerm(
    placement.keys,
    identitySlice,
    option,
  );
  const effectiveDate = effectiveDateKey
    ? text(identitySlice[effectiveDateKey])
    : "";

  const summaryParts = [
    term ? `Term ${term}` : null,
    effectiveDate ? `Effective ${formatTermDate(effectiveDate)}` : null,
    `${actions.length} ${actions.length === 1 ? "action" : "actions"}`,
  ].filter((p): p is string => !!p);

  const actionSummary = describeActionSummary(first, binding, identitySlice);

  const runLevelTermField: FieldSchema = {
    key: placement.keys[0] ?? "policy_number",
    label: termFieldLabel,
    type: "string",
    required: true,
  };

  // Read the date from the ACTION only. Falling back to run-level would let
  // a fresh brief paint on this field before the create-rule rebuilds the
  // action, so RE-ANALYSE could look done while the action card is still gone.
  const effectiveDateValue =
    effectiveDateKey && first && typeof first[effectiveDateKey] === "string"
      ? toIsoDateInput(first[effectiveDateKey] as string)
      : "";
  const effectiveDateError = effectiveDateKey
    ? actionErrors[0]?.[effectiveDateKey]
    : undefined;
  const effectiveDateConfidence = effectiveDateKey
    ? fieldConfidence?.[effectiveDateKey]
    : undefined;
  const effectiveDateLoading = effectiveDateKey
    ? fieldLoading?.[effectiveDateKey]
    : undefined;
  const conf = effectiveDateConfidence
    ? CONFIDENCE_CHIP[effectiveDateConfidence]
    : null;

  return (
    <CollapsibleGroup
      testId="launch-policy-group"
      title={policy ? `Policy ${policy}` : "Policy"}
      summary={summaryParts.join(" · ")}
    >
      {placement.level !== null ? (
        <div data-testid="launch-policy-term-field">
          <PolicyTermField
            binding={binding}
            helperValues={identitySlice}
            policyOptions={policyOptions}
            writePatch={(patch) => writePatch(0, patch)}
            submitting={submitting}
            field={runLevelTermField}
          />
        </div>
      ) : null}

      {effectiveDateKey ? (
        <div className="flex flex-col gap-1">
          <label
            htmlFor="launch-effective-date"
            className="flex items-center gap-1.5 text-xs font-bold text-primary-gray-light-700"
          >
            {effectiveDateLabel}
            <FieldHelpTooltip
              text={EFFECTIVE_DATE_HELP}
              testId="launch-effective-date-help"
            />
            {conf ? (
              <span
                className={`inline-flex items-center gap-[3px] text-2xs font-medium ${conf.text}`}
                title={`${conf.label} confidence`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${conf.dot}`} />
                {conf.label}
              </span>
            ) : null}
            {effectiveDateLoading ? (
              <span
                className="ml-0.5 inline-block h-2.5 w-2.5 animate-spin rounded-full border border-primary-gray-light-200 border-t-primary-gray-light-500"
                role="status"
                aria-label="Loading prefill value"
              />
            ) : null}
          </label>
          <input
            id="launch-effective-date"
            type="date"
            value={effectiveDateValue}
            disabled={submitting}
            onChange={(e) =>
              writePatch(0, { [effectiveDateKey]: e.target.value })
            }
            className="w-full touch-target rounded-lg border border-primary-gray-light-300 bg-base-white px-3 py-2 text-base text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700 sm:text-sm"
          />
          {effectiveDateError ? (
            <span
              className="text-xs text-primary-error-700"
              role="alert"
              data-testid={`field-error-${effectiveDateKey}`}
            >
              {effectiveDateError}
            </span>
          ) : null}
        </div>
      ) : null}

      <CollapsibleGroup
        testId="launch-actions-list"
        title="Mortgagee action"
        summary={actionSummary}
      >
        {actions.map((action, index) => (
          <ActionCard
            key={index}
            binding={binding}
            index={index}
            action={action}
            helperValues={mergeLaunchSlice(binding, runLevelValues, action)}
            onChangeAction={(next) => onChangeAction(index, next)}
            writePatch={(patch) => writePatch(index, patch)}
            policyOptions={policyOptions}
            submitting={submitting}
            fieldConfidence={fieldConfidence}
            fieldLoading={fieldLoading}
            fieldErrors={actionErrors[index]}
            renderTermField={false}
            oldInterestLabel={oldInterestLabel}
          />
        ))}
        <AddActionAffordance
          maxActions={binding.maxActions}
          actionCount={actions.length}
          onAdd={onAddAction}
          disabled={submitting}
        />
      </CollapsibleGroup>
    </CollapsibleGroup>
  );
}
